import { useEffect, useMemo, useState } from "react";
import { EmptyList } from "src/components/EmptyList";
import Loading from "src/components/Loading";
import {
  NotifyMyEmployeesType,
  useSelectEmployeesContext,
} from "src/pages/DashboardPage/acadia/notificationPage/SelectEmployees.mobile";
import { FlexCol } from "src/utils/flexLayouts";
import { twMerge } from "tailwind-merge";

export default function UsersTableWithFiltersMobile({
  items,
}: {
  items: NotifyMyEmployeesType[];
}) {
  const { items: selectedItems, addItems, addItem, removeItem, removeItems } =
    useSelectEmployeesContext();
  const [checkedAll, setCheckedAll] = useState(false);

  function checkAll(state: boolean) {
    if (state) {
      addItems(items);
    } else {
      removeItems(items);
    }

    setCheckedAll(state);
  }

  function handleCheckItem(item: NotifyMyEmployeesType, isChecked: boolean) {
    if (isChecked) {
      addItem(item);
    } else {
      removeItem(item.id);
    }
  }

  useEffect(() => {
    const isEveryBodySelected =
      items.length > 0 && items.every((item) => selectedItems.has(item.id));

    setCheckedAll(isEveryBodySelected);
  }, [items, selectedItems]);

  const sortedItems = useMemo(
    () =>
      [...items].sort((a, b) =>
        selectedItems.has(a.id) > selectedItems.has(b.id)
          ? -1
          : selectedItems.has(a.id) < selectedItems.has(b.id)
            ? 1
            : 0,
      ),
    [items, selectedItems],
  );

  if (!sortedItems) {
    return <Loading />;
  }

  if (!sortedItems.length) {
    return <EmptyList title="Сотрудники не найдены" />;
  }

  return (
    <FlexCol className="divide-y">
      <button
        type="button"
        onClick={() => checkAll(!checkedAll)}
        className="flex items-center gap-3 px-5 py-3 text-left"
      >
        <ItemCheckbox checked={checkedAll} />
        <span className="text-[14px] leading-[20px] text-[#111827]">
          Выбрать всех
        </span>
      </button>

      {sortedItems.map((item) => (
        <EmployeeRow
          key={item.id}
          item={item}
          isChecked={selectedItems.has(item.id)}
          onChange={handleCheckItem}
        />
      ))}
    </FlexCol>
  );
}

function EmployeeRow({
  item,
  isChecked,
  onChange,
}: {
  item: NotifyMyEmployeesType;
  isChecked: boolean;
  onChange: (item: NotifyMyEmployeesType, isChecked: boolean) => void;
}) {
  const [checked, setChecked] = useState(isChecked);

  useEffect(() => {
    setChecked(isChecked);
  }, [isChecked]);

  function toggle(nextState: boolean) {
    setChecked(nextState);
    onChange(item, nextState);
  }

  return (
    <button
      type="button"
      onClick={() => toggle(!checked)}
      className="flex w-full items-start gap-3 px-5 py-3 text-left"
    >
      <ItemCheckbox checked={checked} />
      <div className="min-w-0 grow">
        <div className="flex min-w-0 items-center gap-2">
          <EmployeeAvatar shortName={item.user.shortName} />
          <span className="min-w-0 break-words whitespace-normal text-[14px] leading-[20px] text-[#111827]">
            {item.user.name}
          </span>
        </div>
        <div className="mt-1 min-w-0 text-[13px] leading-[18px] text-[#637381]">
          <div>{item.location}</div>
          <div>{item.department}</div>
          <div>{item.position}</div>
        </div>
      </div>
    </button>
  );
}

function EmployeeAvatar({ shortName }: { shortName: string }) {
  return (
    <div className="mt-0.5 flex h-7.5 w-7.5 shrink-0 items-center justify-center rounded-full bg-darkblue text-[10px] font-medium text-white">
      {shortName}
    </div>
  );
}

function ItemCheckbox({ checked }: { checked: boolean }) {
  return (
    <div
      className={twMerge(
        checked ? "border-darkblue" : "border-[#DFE4EA]",
        "mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded border bg-white",
      )}
    >
      <span className={twMerge(checked ? "opacity-100" : "opacity-0")}>
        <svg
          width="11"
          height="8"
          viewBox="0 0 11 8"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M10.0915 0.951972L10.0867 0.946075L10.0813 0.940568C9.90076 0.753564 9.61034 0.753146 9.42927 0.939309L4.16201 6.22962L1.58507 3.63469C1.40401 3.44841 1.11351 3.44879 0.932892 3.63584C0.755703 3.81933 0.755703 4.10875 0.932892 4.29224L0.932878 4.29225L0.934851 4.29424L3.58046 6.95832C3.73676 7.11955 3.94983 7.2 4.1473 7.2C4.36196 7.2 4.55963 7.11773 4.71406 6.9584L10.0468 1.60234C10.2436 1.4199 10.2421 1.1339 10.0915 0.951972ZM4.2327 6.30081L4.2317 6.2998C4.23206 6.30015 4.23237 6.30049 4.23269 6.30082L4.2327 6.30081Z"
            fill="#012169"
            stroke="#012169"
            strokeWidth="0.4"
          />
        </svg>
      </span>
    </div>
  );
}
